/**
 * KomunikatorWidget - globalny widget komunikatora V3SYS.
 *
 * Kontener: przycisk w prawym dolnym rogu (zwiniety) albo panel przypiety do
 * prawej krawedzi i do DOLU ekranu (rozwiniety). Panel NIGDY nie siega wyzej niz
 * pod topbar (56 px) - gorna nawigacja zostaje w calosci widoczna i klikalna.
 *
 * ODPYTYWANIE (swiadomie BEZ WebSocket/SSE - gunicorn ma worker_class="sync"
 * i tylko 2 workery, stale polaczenie zjadloby caly serwer):
 *   - panel otwarty  -> GET /nieprzeczytane co 3 s,
 *   - panel zwiniety -> co 20 s,
 *   - karta w tle    -> CALKOWITY STOP (document.visibilityState),
 *   - blad sieci     -> wykladnicze wydluzanie odstepu do 60 s, bez zapetlenia.
 * Petla stoi na setTimeout (nie setInterval), bo tylko tak da sie zmieniac
 * odstep w locie. Cleanup useEffect kasuje timer i nasluchy - zero wyciekow.
 *
 * POWIADOMIENIA: licznik zawsze; dzwiek (WebAudio, bez pliku audio) i dymek
 * TYLKO poza ekranem POS. Na /pos calkowita cisza - decyzja wlasciciela.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import ListaRozmow from './ListaRozmow';
import OknoRozmowy from './OknoRozmowy';
import {
  zalacznikBlobUrl,
  pobierzRozmowy,
  pobierzNieprzeczytane,
  ukryjRozmowe,
} from '../../services/komunikatorService';
import './Komunikator.css';

const KLUCZ_OTWARTY = 'v3sys_komunikator_otwarty';
const KLUCZ_AKTYWNA = 'v3sys_komunikator_aktywna';

const OKRES_OTWARTY = 3000;
const OKRES_ZWINIETY = 20000;
const MAX_OKRES = 60000;
const PROG_WASKI = 900;

// Trasy bez chrome aplikacji (wydruki, kolektor) - tam widget tylko przeszkadza.
const TRASY_UKRYTE = ['/login', '/kolektor', '/pos/receipt/', '/pos/return-print/', '/pos/correction-print/'];

const czytajLocalStorage = (klucz, domyslna) => {
  try {
    const v = window.localStorage.getItem(klucz);
    return v === null ? domyslna : v;
  } catch (e) {
    return domyslna;
  }
};

const zapiszLocalStorage = (klucz, wartosc) => {
  try { window.localStorage.setItem(klucz, String(wartosc)); } catch (e) { /* tryb prywatny */ }
};

/**
 * Sciezka bez zaleznosci od react-router.
 * Widget moze zostac osadzony rowniez POZA <Router> - useLocation() rzucilby
 * wtedy wyjatkiem i wywalil cala aplikacje. Odczyt window.location + tani
 * interwal (porownanie stringow, zero sieci) jest odporny na oba warianty.
 */
const useSciezka = () => {
  const [sciezka, setSciezka] = useState(() => window.location.pathname);
  useEffect(() => {
    const sprawdz = () => {
      const aktualna = window.location.pathname;
      setSciezka((poprzednia) => (poprzednia === aktualna ? poprzednia : aktualna));
    };
    const timer = setInterval(sprawdz, 800);
    window.addEventListener('popstate', sprawdz);
    return () => {
      clearInterval(timer);
      window.removeEventListener('popstate', sprawdz);
    };
  }, []);
  return sciezka;
};

const jestNaPos = () => {
  const p = window.location.pathname || '';
  return p === '/pos' || p.startsWith('/pos/');
};

// ---------------------------------------------------------------------------
// Podglad obrazu w pelnym rozmiarze
// ---------------------------------------------------------------------------

const PodgladObrazu = ({ zalacznik, onZamknij }) => {
  const [url, setUrl] = useState(null);
  const [blad, setBlad] = useState(false);

  useEffect(() => {
    let zywy = true;
    Promise.resolve(zalacznikBlobUrl(zalacznik.id, false))
      .then((u) => { if (zywy) setUrl(u); })
      .catch(() => { if (zywy) setBlad(true); });
    return () => { zywy = false; };
  }, [zalacznik.id]);

  useEffect(() => {
    const naKlawisz = (e) => { if (e.key === 'Escape') onZamknij(); };
    document.addEventListener('keydown', naKlawisz);
    return () => document.removeEventListener('keydown', naKlawisz);
  }, [onZamknij]);

  return (
    <div className="kom-podglad-tlo" onClick={onZamknij} role="presentation">
      <div className="kom-podglad-ramka" onClick={(e) => e.stopPropagation()} role="presentation">
        <div className="kom-podglad-belka">
          <span className="kom-podglad-nazwa">{zalacznik.nazwa}</span>
          <button
            type="button"
            className="kom-ikona-btn"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
            onClick={onZamknij}
            title="Zamknij podgląd"
          >
            ×
          </button>
        </div>
        {blad && <div className="kom-info">Nie udało się wczytać obrazu.</div>}
        {!blad && !url && <div className="kom-info">Wczytywanie…</div>}
        {url && <img src={url} alt={zalacznik.nazwa || 'Zdjęcie'} />}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Widget
// ---------------------------------------------------------------------------

const KomunikatorWidget = () => {
  const { isAuthenticated } = useAuth();
  // Uprawnienie 'komunikator' ma domyslnie kazdy (zasiewa je migracja 035), ale
  // wlasciciel moze je komus ODEBRAC w panelu Uprawnien. Wtedy backend odpowiada
  // 403 na /rozmowy i /kontakty - widget nie moze wisiec na ekranie i odpytywac
  // w kolko, wiec chowamy go tak samo jak przed zalogowaniem.
  const { hasPermission } = usePermissions();
  const maDostep = hasPermission('komunikator', 'dostep');
  const sciezka = useSciezka();

  const [otwarty, setOtwarty] = useState(() => czytajLocalStorage(KLUCZ_OTWARTY, '0') === '1');
  const [rozmowy, setRozmowy] = useState([]);
  const [aktywnaId, setAktywnaId] = useState(() => {
    const v = Number(czytajLocalStorage(KLUCZ_AKTYWNA, '0'));
    return v > 0 ? v : null;
  });
  const [licznik, setLicznik] = useState(0);
  const [ladowanieListy, setLadowanieListy] = useState(false);
  const [bladListy, setBladListy] = useState(null);
  const [sygnal, setSygnal] = useState(0);
  const [waski, setWaski] = useState(() => window.innerWidth < PROG_WASKI);
  const [widokMobilny, setWidokMobilny] = useState('lista');
  const [podglad, setPodglad] = useState(null);

  const otwartyRef = useRef(otwarty);
  const aktywnaIdRef = useRef(aktywnaId);
  const rozmowyRef = useRef(rozmowy);
  const ostatnieIdRef = useRef(new Map());
  const pierwszyPollRef = useRef(true);
  const bledyRef = useRef(0);
  const audioRef = useRef(null);
  const ostatnieOdswiezenieRef = useRef(0);
  const zywyRef = useRef(true);

  useEffect(() => { otwartyRef.current = otwarty; }, [otwarty]);
  useEffect(() => { aktywnaIdRef.current = aktywnaId; }, [aktywnaId]);
  useEffect(() => { rozmowyRef.current = rozmowy; }, [rozmowy]);
  useEffect(() => {
    zywyRef.current = true;
    return () => { zywyRef.current = false; };
  }, []);

  // --- responsywnosc: ponizej 900 px przelaczamy widoki zamiast dwoch kolumn
  useEffect(() => {
    const naZmiane = () => setWaski(window.innerWidth < PROG_WASKI);
    window.addEventListener('resize', naZmiane);
    return () => window.removeEventListener('resize', naZmiane);
  }, []);

  // --- trwalosc stanu panelu miedzy przeladowaniami
  useEffect(() => { zapiszLocalStorage(KLUCZ_OTWARTY, otwarty ? '1' : '0'); }, [otwarty]);
  useEffect(() => { zapiszLocalStorage(KLUCZ_AKTYWNA, aktywnaId || 0); }, [aktywnaId]);

  // --- krotki sygnal dzwiekowy bez pliku audio
  const zagrajSygnal = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioRef.current) audioRef.current = new AC();
      const ctx = audioRef.current;
      // Przegladarka blokuje audio przed pierwszym gestem uzytkownika -
      // resume() jest best-effort, brak dzwieku nie moze niczego wywalic.
      if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
      const teraz = ctx.currentTime;
      const osc = ctx.createOscillator();
      const wzm = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, teraz);
      osc.frequency.setValueAtTime(1175, teraz + 0.09);
      wzm.gain.setValueAtTime(0.0001, teraz);
      wzm.gain.exponentialRampToValueAtTime(0.08, teraz + 0.02);
      wzm.gain.exponentialRampToValueAtTime(0.0001, teraz + 0.24);
      osc.connect(wzm);
      wzm.connect(ctx.destination);
      osc.start(teraz);
      osc.stop(teraz + 0.26);
    } catch (e) {
      /* dzwiek jest dodatkiem - cisza zamiast bledu */
    }
  }, []);

  // --- lista rozmow (ten endpoint zaklada kanaly i uczestnictwo)
  const odswiezListe = useCallback(async (wymuszone = false) => {
    const teraz = Date.now();
    if (!wymuszone && teraz - ostatnieOdswiezenieRef.current < 4000) return;
    ostatnieOdswiezenieRef.current = teraz;
    setLadowanieListy(true);
    try {
      const d = (await pobierzRozmowy()) || {};
      if (!zywyRef.current) return;
      setRozmowy(Array.isArray(d.rozmowy) ? d.rozmowy : []);
      setLicznik(Number(d.total) || 0);
      setBladListy(null);
      bledyRef.current = 0;
    } catch (e) {
      if (!zywyRef.current) return;
      setBladListy(e.message || 'Brak połączenia z komunikatorem');
    } finally {
      if (zywyRef.current) setLadowanieListy(false);
    }
  }, []);

  // --- reakcja na wynik pollingu
  const zastosujNieprzeczytane = useCallback((odpowiedz) => {
    const d = odpowiedz && Array.isArray(odpowiedz.rozmowy)
      ? odpowiedz
      : { total: 0, rozmowy: [] };
    const mapa = new Map(d.rozmowy.map((r) => [r.id, r]));

    setRozmowy((prev) => prev.map((r) => {
      const u = mapa.get(r.id);
      return { ...r, nieprzeczytane: u ? u.nieprzeczytane : 0 };
    }));
    setLicznik(Number(d.total) || 0);

    // Nowe = rozmowa, ktorej ostatnia_id przeskoczyla ponad zapamietana wartosc.
    const nowe = d.rozmowy.filter((r) => (
      (r.ostatnia_id || 0) > (ostatnieIdRef.current.get(r.id) || 0)
    ));
    d.rozmowy.forEach((r) => ostatnieIdRef.current.set(r.id, r.ostatnia_id || 0));

    const pierwszy = pierwszyPollRef.current;
    pierwszyPollRef.current = false;
    if (!nowe.length) return;

    // zajawki na liscie sa nieaktualne - odswiez (throttle 4 s w srodku)
    odswiezListe(false);

    // otwarta rozmowa ma dociagnac nowe wiadomosci
    if (aktywnaIdRef.current && mapa.has(aktywnaIdRef.current)) {
      setSygnal((s) => s + 1);
    }

    // Po zalogowaniu nie dzwonimy za zalegla historie - pierwszy odczyt tylko
    // zapamietuje stan.
    if (pierwszy) return;

    const doPowiadomienia = nowe.filter((r) => {
      if (r.wyciszona) return false;
      // rozmowa jest wlasnie na ekranie i uzytkownik na nia patrzy
      const naEkranie = otwartyRef.current
        && r.id === aktywnaIdRef.current
        && document.visibilityState === 'visible';
      return !naEkranie;
    });
    if (!doPowiadomienia.length) return;

    // DECYZJA WLASCICIELA: na ekranie POS calkowita cisza (bez dzwieku i dymka).
    // Licznik przy przycisku dziala oczywiscie dalej.
    if (jestNaPos()) return;

    zagrajSygnal();

    const pierwszaNowa = doPowiadomienia[0];
    const opis = rozmowyRef.current.find((r) => r.id === pierwszaNowa.id);
    const nazwa = (opis && opis.nazwa) || 'Komunikator';
    const wiecej = doPowiadomienia.length > 1 ? ` (+${doPowiadomienia.length - 1})` : '';
    toast((t) => (
      <span
        style={{ cursor: 'pointer' }}
        onClick={() => {
          toast.dismiss(t.id);
          setAktywnaId(pierwszaNowa.id);
          setWidokMobilny('rozmowa');
          setOtwarty(true);
        }}
        role="presentation"
      >
        Nowa wiadomość — {nazwa}{wiecej}
      </span>
    ), { id: 'komunikator-nowa', icon: '💬', duration: 4500 });
  }, [odswiezListe, zagrajSygnal]);

  // --- pierwsze pobranie listy po zalogowaniu
  useEffect(() => {
    if (!isAuthenticated || !maDostep) {
      setRozmowy([]);
      setLicznik(0);
      ostatnieIdRef.current = new Map();
      pierwszyPollRef.current = true;
      return;
    }
    odswiezListe(true);
  }, [isAuthenticated, maDostep, odswiezListe]);

  // --- petla pollingu
  useEffect(() => {
    if (!isAuthenticated || !maDostep) return undefined;

    let zywa = true;
    let timer = null;

    const okres = () => {
      const baza = otwartyRef.current ? OKRES_OTWARTY : OKRES_ZWINIETY;
      if (!bledyRef.current) return baza;
      return Math.min(baza * Math.pow(2, bledyRef.current), MAX_OKRES);
    };

    const zaplanuj = (opoznienie) => {
      if (!zywa) return;
      clearTimeout(timer);
      timer = setTimeout(tik, opoznienie);
    };

    async function tik() {
      if (!zywa) return;
      // Karta w tle - NIE odpytujemy wcale. Wznowienie robi visibilitychange.
      if (document.visibilityState !== 'visible') return;
      try {
        const d = await pobierzNieprzeczytane();
        if (!zywa) return;
        bledyRef.current = 0;
        zastosujNieprzeczytane(d);
      } catch (e) {
        if (!zywa) return;
        // Bez toastu i bez znikania panelu - tylko wolniejsze proby.
        // 401 obsluguje AuthContext/ProtectedRoute; my przestajemy dobijac.
        bledyRef.current = e && e.isAuthError ? 5 : Math.min(bledyRef.current + 1, 5);
      } finally {
        if (zywa && document.visibilityState === 'visible') zaplanuj(okres());
      }
    }

    const naWidocznosc = () => {
      if (document.visibilityState === 'visible') {
        bledyRef.current = 0;
        zaplanuj(200);
      } else {
        clearTimeout(timer);
      }
    };

    zaplanuj(400);
    document.addEventListener('visibilitychange', naWidocznosc);

    return () => {
      zywa = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', naWidocznosc);
    };
  }, [isAuthenticated, maDostep, otwarty, zastosujNieprzeczytane]);

  // --- akcje UI
  const przelacz = useCallback(() => {
    setOtwarty((v) => {
      const nowy = !v;
      if (nowy) odswiezListe(true);
      return nowy;
    });
  }, [odswiezListe]);

  const wybierzRozmowe = useCallback((id) => {
    setAktywnaId(id);
    setWidokMobilny('rozmowa');
  }, []);

  const poOtwarciuDm = useCallback((rozmowa) => {
    // Rozmowe wstawiamy do listy OD RAZU, zanim wroci odswiezenie z serwera.
    // Efekt nizej ("rozmowa moze zniknac z listy") kasuje aktywnaId dla kazdej
    // rozmowy, ktorej NIE MA w aktualnej liscie - a swiezo otwarta rozmowa
    // prywatna (nowa albo wczesniej zamknieta) trafia tam dopiero po round-tripie
    // GET /rozmowy. Bez tego panel mrugal i natychmiast wracal do listy.
    setRozmowy((prev) => (
      prev.some((r) => r.id === rozmowa.id)
        ? prev
        : [...prev, {
          id: rozmowa.id,
          typ: rozmowa.typ || 'prywatna',
          nazwa: rozmowa.nazwa || rozmowa.rozmowca || 'Rozmowa',
          zamykalna: rozmowa.zamykalna !== false,
          nieprzeczytane: 0,
          wyciszona: false,
          ostatnia_tresc: null,
          ostatnia_at: null,
          ostatnia_autor: null,
        }]
    ));
    setAktywnaId(rozmowa.id);
    setWidokMobilny('rozmowa');
    odswiezListe(true);
  }, [odswiezListe]);

  const zamknijRozmowe = useCallback(async (id) => {
    const kopia = rozmowyRef.current;
    setRozmowy((prev) => prev.filter((r) => r.id !== id));
    if (aktywnaIdRef.current === id) {
      setAktywnaId(null);
      setWidokMobilny('lista');
    }
    try {
      await ukryjRozmowe(id);
    } catch (e) {
      // przywroc liste - rozmowa nie zostala zamknieta po stronie serwera
      if (zywyRef.current) setRozmowy(kopia);
      toast.error(e.message || 'Nie udało się zamknąć rozmowy');
    }
  }, []);

  const oznaczonoPrzeczytane = useCallback((id) => {
    const r = rozmowyRef.current.find((x) => x.id === id);
    const ile = r ? (r.nieprzeczytane || 0) : 0;
    if (ile > 0) setLicznik((l) => Math.max(0, l - ile));
    setRozmowy((prev) => prev.map((x) => (x.id === id ? { ...x, nieprzeczytane: 0 } : x)));
  }, []);

  const aktywna = useMemo(
    () => rozmowy.find((r) => r.id === aktywnaId) || null,
    [rozmowy, aktywnaId]
  );

  // DOMYSLNY KANAL = "Cala siec".
  // Pierwszego dnia na produkcji panel otwieral sie na "Wybierz rozmowe z listy",
  // wiec pracownik klikal cokolwiek - i odpisal na kanale swojego sklepu w
  // przekonaniu, ze pisze do wszystkich. Reszta sieci tego nie zobaczyla.
  // Domyslnie stajemy zawsze na kanale ogolnym; kanal sklepu jest swiadomym
  // wyborem, a raz wybrana rozmowa zostaje zapamietana (localStorage), wiec
  // to nie odbiera nikomu mozliwosci pisania do wlasnego sklepu.
  useEffect(() => {
    if (aktywnaId || !rozmowy.length || waski) return;
    const globalny = rozmowy.find((r) => r.typ === 'globalny');
    if (globalny) setAktywnaId(globalny.id);
  }, [rozmowy, aktywnaId, waski]);

  // Rozmowa moze zniknac z listy (np. zamknieta w innej karcie) - nie zostawiamy
  // pustego okna z widmem.
  useEffect(() => {
    if (aktywnaId && rozmowy.length && !rozmowy.some((r) => r.id === aktywnaId)) {
      setAktywnaId(null);
      setWidokMobilny('lista');
    }
  }, [rozmowy, aktywnaId]);

  const ukryty = !isAuthenticated || !maDostep || TRASY_UKRYTE.some((t) => (
    sciezka === t || sciezka.startsWith(t)
  ));
  if (ukryty) return null;

  const pokazListe = !waski || widokMobilny === 'lista';
  const pokazOkno = !waski || widokMobilny === 'rozmowa';

  const tresc = (
    <>
      {!otwarty && (
        <>
          {/* Alarm nieprzeczytanych: pulsujaca czerwona plakietka OBOK przycisku.
              Sygnal wylacznie wizualny - dziala takze na POS, bo tam kasjer
              spedza wieksza czesc zmiany i wyciszenie odbieraloby mu sens.
              Dzwiek i dymek pozostaja na kasie wyciszone (patrz naPos wyzej). */}
          {licznik > 0 && (
            <button
              type="button"
              className="kom-alarm-etykieta"
              /* Tlo MUSI byc liniowe. v3sys-theme.css:962 wymusza wlasne tlo na
                 kazdym <button> regula z !important i wysoka swoistoscia (dlugi
                 lancuch :not) - klasa z !important jej NIE pokona. Jedyna furtka
                 to selektor :not([style*="background"]), czyli styl liniowy.
                 Bez tego alarm bylby bialy zamiast czerwonego. */
              style={{ background: '#DC2626', color: '#fff' }}
              onClick={przelacz}
              title="Masz nieprzeczytane wiadomości - kliknij, aby otworzyć"
              aria-hidden="true"
              tabIndex={-1}
            >
              <span className="kom-alarm-wykrzyknik">!</span>
              <span className="kom-alarm-etykieta-tekst">
                {licznik === 1 ? 'Nowa wiadomość' : `Nowe wiadomości (${licznik > 99 ? '99+' : licznik})`}
              </span>
            </button>
          )}
          <button
            type="button"
            className={`kom-fab${licznik > 0 ? ' kom-fab-alarm' : ''}`}
            /* Tlo zawsze LINIOWE - patrz komentarz przy plakietce wyzej.
               W trybie alarmu czerwien #DC2626, poza nim kolor motywu. */
            style={licznik > 0
              ? { background: '#DC2626', border: '1px solid #DC2626', color: '#fff' }
              : { background: 'var(--primary)', border: '1px solid var(--primary)', color: '#fff' }}
            onClick={przelacz}
            title={licznik > 0 ? `Komunikator - ${licznik} nieprzeczytanych` : 'Komunikator'}
            aria-label={licznik > 0 ? `Komunikator, ${licznik} nieprzeczytanych` : 'Komunikator'}
          >
            <span className="kom-fab-ikona">{licznik > 0 ? '❗' : '💬'}</span>
            {licznik > 0 && (
              <span className="kom-fab-licznik">
                {licznik > 99 ? '99+' : licznik}
              </span>
            )}
          </button>
        </>
      )}

      {otwarty && (
        <div className={`kom-panel ${aktywna && !waski ? 'kom-panel-szeroki' : ''}`}>
          <div className="kom-panel-naglowek">
            <div className="kom-panel-gora">
              <span className="kom-panel-tytul">💬 Komunikator</span>
              <div className="kom-panel-akcje">
                <button
                  type="button"
                  className="kom-ikona-btn"
                  style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
                  onClick={() => odswiezListe(true)}
                  title="Odśwież listę rozmów"
                  aria-label="Odśwież"
                >
                  ⟳
                </button>
                <button
                  type="button"
                  className="kom-ikona-btn"
                  style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}
                  onClick={przelacz}
                  title="Zwiń komunikator"
                  aria-label="Zwiń"
                >
                  ×
                </button>
              </div>
            </div>
            {/* Wymog prawny - notka musi byc stale widoczna, ale dyskretna. */}
            <div className="kom-nota-archiwum">
              Rozmowy są archiwizowane i dostępne dla administratora.
            </div>
          </div>

          <div className="kom-panel-tresc">
            {pokazListe && (
              <ListaRozmow
                rozmowy={rozmowy}
                aktywnaId={aktywnaId}
                ladowanie={ladowanieListy}
                blad={bladListy}
                onWybierz={wybierzRozmowe}
                onZamknij={zamknijRozmowe}
                onOtwarto={poOtwarciuDm}
                onOdswiez={() => odswiezListe(true)}
              />
            )}
            {pokazOkno && (
              <OknoRozmowy
                rozmowa={aktywna}
                sygnal={sygnal}
                waski={waski}
                onWstecz={() => setWidokMobilny('lista')}
                onPrzeczytane={oznaczonoPrzeczytane}
                onZmianaListy={() => odswiezListe(false)}
                onPodglad={setPodglad}
              />
            )}
          </div>
        </div>
      )}

      {podglad && <PodgladObrazu zalacznik={podglad} onZamknij={() => setPodglad(null)} />}
    </>
  );

  // Portal do <body>: panel jest position:fixed, a osadzony w drzewie Layoutu
  // moglby trafic pod przodka z transform/overflow i przestac sie kleic do
  // krawedzi ekranu.
  return createPortal(<div className="kom-widget">{tresc}</div>, document.body);
};

export default KomunikatorWidget;
